import React, { useState, useRef, useEffect } from 'react';
import { LayoutTemplate, Loader2, FileText } from 'lucide-react';
import { useLazyGetTemplatesDropdownQuery } from '../../../../store/apis/survey.api';

interface LoadTemplateMenuProps {
  onSelect: (templateId: string) => void;
  align?: 'left' | 'right';
  label?: string;
}

/** Shared Load-Template dropdown (Figma dc.html:4445): lazy templates query + menu. */
const LoadTemplateMenu: React.FC<LoadTemplateMenuProps> = ({
  onSelect,
  align = 'left',
  label = 'Load Template',
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const [fetchTemplates, { data: templates = [], isLoading }] = useLazyGetTemplatesDropdownQuery();

  useEffect(() => {
    if (open) fetchTemplates();
  }, [open, fetchTemplates]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="h-[34px] px-[13px] flex items-center gap-[7px] border border-[var(--color-layout-dropdown-badge-border)] bg-[var(--color-bg-base)] rounded-[9px] text-[12px] font-bold text-[var(--color-text-strong)] cursor-pointer hover:border-[var(--color-border-input-hover)] transition-colors focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none"
      >
        <LayoutTemplate className="w-3.5 h-3.5 text-[var(--color-primary-700)]" />
        {label}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute top-full mt-1.5 z-20 w-[220px] bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-[13px] shadow-[var(--shadow-lg)] p-1.5 ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-4 px-4">
              <Loader2 className="w-4 h-4 animate-spin text-[var(--color-text-tertiary)] mr-2" />
              <span className="text-body-sm text-[var(--color-text-secondary)]">Loading templates...</span>
            </div>
          ) : templates.length === 0 ? (
            <div className="py-4 px-4 text-center">
              <FileText className="w-6 h-6 text-[var(--color-text-disabled)] mx-auto mb-2" />
              <p className="text-body-sm text-[var(--color-text-secondary)]">No templates available</p>
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              {templates.map((template) => (
                <button
                  key={template._id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onSelect(template._id);
                  }}
                  className="w-full text-left px-2.5 py-2.5 rounded-[9px] text-[12.5px] font-semibold text-[var(--color-text-strong)] hover:bg-[var(--color-bg-muted)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none flex items-center gap-2 cursor-pointer border-0 bg-transparent"
                >
                  <FileText className="w-4 h-4 text-[var(--color-primary-700)] flex-none" />
                  <span className="truncate">{template.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LoadTemplateMenu;
